import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  RotateCcw,
  Download,
  ShieldAlert,
  ClipboardCopy,
  Images,
  Save,
  RefreshCw,
} from "lucide-react";
import { getContentSections } from "./content-admin/adminSections";
import { AdminSectionCard } from "./content-admin/AdminSectionCard";
import { VehicleManager } from "./content-admin/VehicleManager";
import {
  loadOverrides,
  saveOverrides,
  clearOverrides,
  AdminVehicleOverride,
} from "./content-admin/adminStore";
import { buildImageRegistry, buildImagePathMap } from "./content-admin/imageRegistry";
import { clearAllImageOverrides, listImageOverrides } from "../lib/imageStore";
import { featuredVehicles } from "../data/featuredVehicles";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

const VEHICLE_IMAGE_PATHS: Record<number, string> = {
  1: "src/assets/vehicles/hilux.jpg",
  2: "src/assets/vehicles/frontier.jpg",
  3: "src/assets/vehicles/corolla.jpg",
  4: "src/assets/vehicles/crv.jpg",
  5: "src/assets/vehicles/civic.jpg",
};

const defaultVehicles = (): AdminVehicleOverride[] =>
  featuredVehicles.map((v) => ({
    id: v.id,
    title: v.title,
    price: v.price,
    location: v.location,
    mileage: v.mileage || "",
    badge: v.badge || "",
    url: v.url,
    image: v.image,
    imagePath: VEHICLE_IMAGE_PATHS[v.id] || "src/assets/vehicles/",
  }));

export default function ContentAdmin() {
  const sections = useMemo(() => getContentSections(), []);
  const [stored, setStored] = useState(() => loadOverrides());
  const [fields, setFields] = useState<Record<string, string>>(stored.fields);
  const [vehicles, setVehicles] = useState<AdminVehicleOverride[]>(
    stored.vehicles && stored.vehicles.length > 0 ? stored.vehicles : defaultVehicles(),
  );
  const [saving, setSaving] = useState(false);

  const getValue = (key: string, fallback: string) => fields[key] ?? fallback;

  const handleFieldChange = (key: string, value: string) => {
    const next = { ...fields, [key]: value };
    setFields(next);
    saveOverrides({ fields: next, vehicles });
  };

  const handleVehiclesChange = (next: AdminVehicleOverride[]) => {
    setVehicles(next);
    saveOverrides({ fields, vehicles: next });
  };

  const handleRestore = () => {
    const confirmed = window.confirm(
      "Esto eliminará todos los cambios locales de textos, enlaces, vehículos e imágenes. ¿Deseas continuar?",
    );
    if (!confirmed) return;
    clearOverrides();
    clearAllImageOverrides();
    setFields({});
    setVehicles(defaultVehicles());
  };

  const buildExportPayload = () => ({
    exportedAt: new Date().toISOString(),
    fields,
    vehicles,
    images: buildImageRegistry(sections, vehicles),
  });

  const handleExport = () => {
    const payload = buildExportPayload();
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "motorpro-content-config.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportImages = () => {
    const registry = buildImageRegistry(sections, vehicles);
    const blob = new Blob([JSON.stringify(registry, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "motorpro-image-registry.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyConfig = async () => {
    const payload = buildExportPayload();
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success("Configuración copiada");
    } catch {
      toast.error("No se pudo copiar la configuración");
    }
  };

  const handleSaveToProject = async () => {
    setSaving(true);
    try {
      const images = listImageOverrides();
      const imagePaths = buildImagePathMap(sections, vehicles);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/content-admin/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fields, vehicles, images, imagePaths }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || "No se pudo guardar en el proyecto");
      }
      (data.warnings || []).forEach((w: string) => toast.warning(w));
      toast.success(
        data.filesWritten.length > 0
          ? `Guardado en el proyecto: ${data.filesWritten.length} archivo(s) actualizado(s).`
          : "No había cambios pendientes por guardar en el proyecto.",
      );
      clearOverrides();
      clearAllImageOverrides();
      setFields({});
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar en el proyecto");
    } finally {
      setSaving(false);
    }
  };

  const handleReloadFromProject = () => {
    const confirmed = window.confirm(
      "Esto descarta los cambios locales aún no guardados en el proyecto y recarga los archivos reales del repositorio. ¿Deseas continuar?",
    );
    if (!confirmed) return;
    clearOverrides();
    clearAllImageOverrides();
    window.location.reload();
  };

  return (
    <div data-testid="content-admin-page" className="min-h-screen bg-motor-mist font-body">
      <header className="sticky top-0 z-20 bg-white border-b border-neutral-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display font-semibold text-motor-ink text-base">
              Panel de mantenimiento — MotorPRO Autos
            </p>
            <p className="text-xs text-neutral-500">Ruta interna: /content-admin (no listada en el menú público)</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/"
              data-testid="back-to-landing-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Volver a la landing
            </Link>
            <button
              data-testid="reload-from-project-button"
              onClick={handleReloadFromProject}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Recargar desde el proyecto
            </button>
            <button
              data-testid="restore-original-button"
              onClick={handleRestore}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar contenido original
            </button>
            <button
              data-testid="copy-config-button"
              onClick={handleCopyConfig}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <ClipboardCopy className="h-3.5 w-3.5" /> Copiar JSON
            </button>
            <button
              data-testid="export-images-button"
              onClick={handleExportImages}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <Images className="h-3.5 w-3.5" /> Exportar lista de imágenes
            </button>
            <button
              data-testid="export-config-button"
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-motor-ink hover:border-motor-red hover:text-motor-red transition-colors"
            >
              <Download className="h-3.5 w-3.5" /> Exportar configuración
            </button>
            <button
              data-testid="save-to-project-button"
              onClick={handleSaveToProject}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-motor-red px-4 py-2 text-xs font-semibold text-white hover:bg-motor-red-dark transition-colors disabled:opacity-60"
            >
              <Save className="h-3.5 w-3.5" /> {saving ? "Guardando..." : "Guardar cambios en el proyecto"}
            </button>
          </div>
        </div>
      </header>

      <Toaster position="top-center" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
        <div
          data-testid="admin-preview-warning"
          className="flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3.5 text-sm text-amber-800"
        >
          <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            Los cambios se guardan como borrador local mientras editas. Presiona{" "}
            <strong>"Guardar cambios en el proyecto"</strong> para escribirlos de forma permanente en
            los archivos reales del repositorio — luego solo falta <code>git add</code>,{" "}
            <code>git commit</code> y <code>git push</code> para que Cloudflare Pages los publique.
          </p>
        </div>

        <div className="mt-6 grid lg:grid-cols-[220px_1fr] gap-6">
          <nav
            data-testid="admin-section-nav"
            className="hidden lg:flex flex-col gap-1 sticky top-24 self-start rounded-2xl border border-neutral-200 bg-white p-3"
          >
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#admin-section-${s.id}`}
                className="rounded-lg px-3 py-2 text-xs font-medium text-neutral-600 hover:bg-motor-mist hover:text-motor-red transition-colors"
              >
                {s.title}
              </a>
            ))}
          </nav>

          <div className="grid gap-5">
            {sections.map((section) => (
              <React.Fragment key={section.id}>
                <AdminSectionCard section={section} getValue={getValue} onChange={handleFieldChange} />
                {section.id === "vehicles" && (
                  <div className="rounded-2xl border border-neutral-200 bg-white p-5 sm:p-6">
                    <VehicleManager vehicles={vehicles} onChange={handleVehiclesChange} />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
